import React, { useState, useEffect } from 'react';
import { FormControl, Select, MenuItem, SelectChangeEvent } from '@mui/material';

export type LanguageCode = 'id' | 'en' | 'ko';

interface LanguageOption {
    code: LanguageCode;
    label: string;
    flag: string;
}

const languages: LanguageOption[] = [
    { code: 'id', label: 'Bahasa Indonesia', flag: '🇮🇩' },
    { code: 'en', label: 'English', flag: '🇺🇸' },
    { code: 'ko', label: '한국어', flag: '🇰🇷' }
];

interface LanguageSelectorProps {
    onChange?: (language: LanguageCode) => void;
}

const LANGUAGE_STORAGE_KEY = 'language';

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ onChange }) => {
    const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>('id');

    useEffect(() => {
        const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY) as LanguageCode;
        if (savedLanguage && languages.some(lang => lang.code === savedLanguage)) {
            setSelectedLanguage(savedLanguage);
        }
    }, []);

    const handleChange = (event: SelectChangeEvent) => {
        const languageCode = event.target.value as LanguageCode;
        setSelectedLanguage(languageCode);
        localStorage.setItem(LANGUAGE_STORAGE_KEY, languageCode);
        
        if (onChange) {
            onChange(languageCode);
        }
    };

    return (
        <FormControl size="small" sx={{ minWidth: 160 }}>
            <Select
                value={selectedLanguage}
                onChange={handleChange}
                displayEmpty
                sx={{ 
                    backgroundColor: 'white',
                    '& .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #e0e0e0'
                    }
                }}
            >
                {languages.map((language) => (
                    <MenuItem key={language.code} value={language.code}>
                        <span style={{ marginRight: '8px' }}>{language.flag}</span>
                        {language.label}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default LanguageSelector;

export const getStoredLanguage = (): LanguageCode => {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY) as LanguageCode;
    return stored && languages.some(lang => lang.code === stored) ? stored : 'id';
};

export const setStoredLanguage = (language: LanguageCode): void => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
};